// Features: M004-F0795, M004-F0796, M004-F0798, M004-F0799 (WebUI #263, slice A: the data layer)
// Tests: T-M004-0414..0426 (model and api rows; see billingProfileModel.test.js and this module's test)
//
// The customer billing-profile client, isolated in this one module. Spec core-ledger-account-gateway 1.27.0 (final, Spec !134 @65a29231).
// Every operation is sent through Core's generic relay (CSLPT): POST /api/v1/orgs/:orgId/modules/ledger/messages with the closed
// envelope { relay_version: 1, type, payload, target_user_id? }. Core forwards `payload` unread to the Ledger as the frame body.
//
// Frame mapping, from the pinned contract (tests/ledger-account-gateway/pinned/openapi.json):
//   customer_billing_profile.get / .set / .history / .preview and draft.list are customer frames: target_user_id = the customer
//     (the frame's targetUserId). The body is the frame body, never carrying user_id.
//
// Answers keep the typed shape the components read: { ...response, data: { success: true, data: <Ledger data> } }, the same as
// statementApi and creditNoteApi, so `response.data.data` is the Ledger's data.
//
// Writes (set) need an Idempotency-Key. The caller generates one per user action and reuses it on retry; this module
// refuses to send a write without one (no request is made). The optimistic guard is `expected_rev` (the LATEST rev the drawer read,
// a pending revision included), not `rev`.
import api from './api';

const messagesPath = (orgId) => `/v1/orgs/${encodeURIComponent(orgId)}/modules/ledger/messages`;
const envelope = (type, payload, targetUserId) => ({
  relay_version: 1, type, payload, ...(targetUserId === undefined ? {} : { target_user_id: targetUserId }),
});
const keyed = (idempotencyKey) => ({ headers: { 'Idempotency-Key': idempotencyKey } });
const typedAnswer = (response) => ({
  ...response,
  data: { success: true, data: response?.data?.data?.payload?.data },
});

// A customer frame names its customer. Refused before any request when it is not a positive integer.
const customerOf = (value) => {
  const id = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  if (!Number.isSafeInteger(id) || id < 1) throw new Error('A customer is required for this billing-profile operation');
  return id;
};

// Copies only the members that are present. JSON drops undefined anyway; this keeps the body free of undefined-valued keys
// and keeps `null` (a reset to the organisation default) as a real value.
const present = (members) => Object.fromEntries(Object.entries(members).filter(([, v]) => v !== undefined));

const requireKey = (idempotencyKey) => {
  if (!idempotencyKey) throw new Error('An idempotency key is required to save a billing profile');
  return idempotencyKey;
};

export const customerBillingProfileApi = {
  // READ (SAL 3). rev 0 and the neutral profile when none is saved.
  get: async (orgId, userId) => {
    const id = customerOf(userId);
    return typedAnswer(await api.post(messagesPath(orgId), envelope('customer_billing_profile.get', {}, id)));
  },

  // WRITE (SAL 4). body: { expected_rev, reason, effective_from?, credit?, groups?, apportionments? }. A member given REPLACES that member.
  // The same idempotencyKey is sent on every retry of one user action.
  set: async (orgId, { user_id: userId, expected_rev: expectedRev, effective_from: effectiveFrom, reason, credit, groups, apportionments }, idempotencyKey) => {
    const id = customerOf(userId);
    const key = requireKey(idempotencyKey);
    const body = present({
      expected_rev: expectedRev, effective_from: effectiveFrom, reason, credit, groups, apportionments,
    });
    return typedAnswer(await api.post(messagesPath(orgId), envelope('customer_billing_profile.set', body, id), keyed(key)));
  },

  // READ (SAL 3). Newest first. before_rev pages: entries with rev below it.
  history: async (orgId, userId, { limit, before_rev: beforeRev } = {}) => {
    const id = customerOf(userId);
    return typedAnswer(await api.post(messagesPath(orgId), envelope('customer_billing_profile.history', present({ limit, before_rev: beforeRev }), id)));
  },

  // READ (SAL 3, no write, no step-up). The CANDIDATE groups and apportionments for "Test this rule"; omitted members come from the saved profile.
  preview: async (orgId, userId, { groups, apportionments } = {}) => {
    const id = customerOf(userId);
    return typedAnswer(await api.post(messagesPath(orgId), envelope('customer_billing_profile.preview', present({ groups, apportionments }), id)));
  },

  // READ (SAL 3). The customer's open drafts (the draft switcher). Frame body is empty.
  draftList: async (orgId, userId) => {
    const id = customerOf(userId);
    return typedAnswer(await api.post(messagesPath(orgId), envelope('draft.list', {}, id)));
  },
};
